import {
  ArrowLeft,
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  ChevronsUpDown,
  Clock,
  CreditCard,
  LogOutIcon,
  type LucideIcon,
  Mail,
  MessageSquare,
  Plug,
  Users,
} from 'lucide-react';
import { lazy, type ReactNode, Suspense, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import { ChatList } from '@/modules/hiring-manager/chat/chatList/ChatList';
import type { usePortalLayoutState } from '@/modules/hiring-manager/hooks/usePortalLayoutState';
import { useCandidateSubmissionsAwaitingReviewQuery } from '@/modules/hiring-manager/queries/useCandidateSubmissionsAwaitingReviewQuery';
import { HELP_CENTER_URL, supportMailto } from '@/modules/support/support';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/modules/ui/components/command/Command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/modules/ui/components/dropdown-menu/DropdownMenu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/modules/ui/components/popover/Popover';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/modules/ui/components/sidebar/Sidebar';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { getInitials } from '@/utils/getInitials';

// Lazy so the Stigg SDK stays out of the portal chunk; see
// PortalBillingProvider for the matching provider split.
const UpgradePlanCta = lazy(() =>
  import('@/modules/billing/components/UpgradePlanCta').then((module) => ({
    default: module.UpgradePlanCta,
  })),
);

interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
}

const primaryNavItems: NavItem[] = [
  { icon: BriefcaseBusiness, label: 'Jobs', path: '/hm' },
  { icon: CalendarDays, label: 'Interviews', path: '/hm/interviews' },
  { icon: MessageSquare, label: 'Outreach', path: '/hm/messages' },
  { icon: Clock, label: 'Availability', path: '/hm/availability' },
];

const coordinationNavPaths = new Set(['/hm/interviews', '/hm/availability']);

const secondaryNavItems: NavItem[] = [
  { icon: Plug, label: 'Integrations', path: '/hm/integrations' },
  { icon: Users, label: 'Workspace', path: '/hm/workspace' },
  { icon: CreditCard, label: 'Billing', path: '/hm/billing' },
];

/* Open in a new tab so the portal stays where the user left it. */
const externalNavItems = [
  { href: HELP_CENTER_URL, icon: BookOpen, label: 'Help' },
  {
    href: supportMailto('Help with Finlay'),
    icon: Mail,
    label: 'Contact support',
  },
];

const isNavItemActive = (item: NavItem, pathname: string) =>
  item.path === '/hm'
    ? pathname === '/hm' || pathname.startsWith('/hm/jobs')
    : pathname === item.path || pathname.startsWith(`${item.path}/`);

/* 32px rows: shadcn's p-4 menu button is too tall for a 240px rail. The
   active row's pale fill barely shows on the white rail, so its label and icon
   carry the brand color too. */
const navButtonClass =
  'h-8 gap-2.5 px-2 text-[13px] font-medium data-[active=true]:text-primary data-[active=true]:[&>svg]:text-primary [&>svg]:text-neutral-500';

type PortalLayoutState = ReturnType<typeof usePortalLayoutState>;

type PortalSidebarProps = Pick<
  PortalLayoutState,
  | 'availableCompanies'
  | 'canReturnToRecruiterDashboard'
  | 'changeTenant'
  | 'hasCoordinationEnabledJobs'
  | 'initials'
  | 'isLoadingCompanies'
  | 'logout'
  | 'selectedTenantName'
  | 'signedInUser'
  | 'tenantSelection'
>;

/**
 * Portal navigation: tenant switcher, the day-to-day pages, and the
 * account-level pages, plan prompt and account menu pinned to the bottom.
 * Collapses off-canvas; the only toggle is the trigger in the layout's top
 * bar.
 */
export function PortalSidebar({
  availableCompanies,
  canReturnToRecruiterDashboard,
  changeTenant,
  hasCoordinationEnabledJobs,
  initials,
  isLoadingCompanies,
  logout,
  selectedTenantName,
  signedInUser,
  tenantSelection,
}: PortalSidebarProps) {
  const location = useLocation();
  const { plan, isLoading, error } = useWorkspacePlan();
  const showMessages = !isLoading && !error && plan !== 'managed';
  const { setOpenMobile } = useSidebar();
  const awaitingReviewQuery = useCandidateSubmissionsAwaitingReviewQuery(
    tenantSelection.selectedTenantId,
  );
  const awaitingReviewCount = awaitingReviewQuery.data?.length ?? 0;

  const fullName = [signedInUser?.firstName, signedInUser?.lastName]
    .filter(Boolean)
    .join(' ');

  const renderNavItem = (item: NavItem, badge?: ReactNode) => (
    <SidebarMenuItem key={item.path}>
      <SidebarMenuButton
        asChild
        className={navButtonClass}
        isActive={isNavItemActive(item, location.pathname)}
      >
        <Link
          onClick={() => setOpenMobile(false)}
          to={`${item.path}${tenantSelection.tenantSearch}`}
        >
          <item.icon />
          <span className="flex-1">{item.label}</span>
          {badge}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  return (
    <Sidebar className="border-r" collapsible="offcanvas">
      <SidebarHeader className="p-3 pb-0">
        <TenantSwitcher
          availableCompanies={availableCompanies}
          canSelectTenant={tenantSelection.canSelectTenant}
          isLoadingCompanies={isLoadingCompanies}
          onChange={(tenantId) => {
            setOpenMobile(false);
            changeTenant(tenantId);
          }}
          selectedTenantId={tenantSelection.selectedTenantId}
          selectedTenantName={selectedTenantName}
        />
      </SidebarHeader>

      <SidebarContent className="px-3 pt-3">
        <SidebarMenu className="gap-0.5">
          {primaryNavItems
            .filter((item) => item.path !== '/hm/messages' || showMessages)
            // Coordination-agent pages stay empty without a coordination-enabled job.
            .filter(
              (item) =>
                !coordinationNavPaths.has(item.path) ||
                hasCoordinationEnabledJobs,
            )
            .map((item) =>
              renderNavItem(
                item,
                item.path === '/hm' && awaitingReviewCount > 0 ? (
                  <span
                    aria-label={`${awaitingReviewCount} candidates waiting for you`}
                    className="text-primary-700 text-xs font-medium tabular-nums"
                  >
                    {awaitingReviewCount}
                  </span>
                ) : undefined,
              ),
            )}
        </SidebarMenu>

        <ChatList
          itemClassName={navButtonClass}
          onNavigate={() => setOpenMobile(false)}
          tenantId={tenantSelection.selectedTenantId}
          tenantSearch={tenantSelection.tenantSearch}
        />
      </SidebarContent>

      <SidebarFooter className="gap-1 p-3">
        <SidebarMenu className="mb-2 gap-0.5">
          {secondaryNavItems.map((item) => renderNavItem(item))}

          {externalNavItems.map(({ href, icon: Icon, label }) => (
            <SidebarMenuItem key={label}>
              <SidebarMenuButton asChild className={navButtonClass}>
                <a
                  href={href}
                  onClick={() => setOpenMobile(false)}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Icon />
                  <span className="flex-1">{label}</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>

        <Suspense fallback={null}>
          <UpgradePlanCta to={`/hm/billing${tenantSelection.tenantSearch}`} />
        </Suspense>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="hover:bg-accent focus-visible:ring-ring/50 flex h-9 w-full cursor-pointer items-center gap-2 rounded-md px-2 text-left outline-none focus-visible:ring-[3px]"
              title={fullName || 'Account'}
              type="button"
            >
              <span className="bg-secondary-150 text-primary-700 grid size-[22px] shrink-0 place-items-center rounded-full text-[10px] font-semibold">
                {initials}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium">
                {fullName}
              </span>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start" className="w-56" side="top">
            <DropdownMenuLabel className="p-2 font-normal">
              <span className="block truncate text-sm">{fullName}</span>
              {signedInUser?.email && (
                <span className="text-muted-foreground block truncate text-xs">
                  {signedInUser.email}
                </span>
              )}
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            {canReturnToRecruiterDashboard && (
              <DropdownMenuItem asChild className="p-2">
                <Link to="/jobs">
                  <ArrowLeft size={16} />
                  <span>Recruiter dashboard</span>
                </Link>
              </DropdownMenuItem>
            )}

            <DropdownMenuItem className="p-2" onClick={logout}>
              <LogOutIcon size={16} />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

interface TenantSwitcherProps extends Pick<
  PortalLayoutState,
  'availableCompanies' | 'isLoadingCompanies' | 'selectedTenantName'
> {
  canSelectTenant: boolean;
  onChange: (tenantId: string) => void;
  selectedTenantId?: string;
}

/**
 * Workspace row at the top of the rail. Users with access to several tenants
 * (staff, multi-company recruiters) get a searchable switcher; everyone else
 * sees their company name.
 */
function TenantSwitcher({
  availableCompanies,
  canSelectTenant,
  isLoadingCompanies,
  onChange,
  selectedTenantId,
  selectedTenantName,
}: TenantSwitcherProps) {
  const [open, setOpen] = useState(false);

  const label = (
    <>
      <span
        aria-hidden
        className="bg-secondary-150 text-primary-700 grid size-5 shrink-0 place-items-center rounded-[5px] text-[9px] font-semibold"
      >
        {getInitials(selectedTenantName ?? 'Finlay')}
      </span>
      {isLoadingCompanies && !selectedTenantName ? (
        <Skeleton className="h-3.5 flex-1" />
      ) : (
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">
          {selectedTenantName ?? 'Finlay'}
        </span>
      )}
    </>
  );

  if (!canSelectTenant) {
    return <div className="flex h-9 items-center gap-2 px-2">{label}</div>;
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <button
          aria-label="Switch company"
          className={cn(
            'hover:bg-accent focus-visible:ring-ring/50 flex h-9 w-full cursor-pointer items-center gap-2 rounded-md px-2 text-left outline-none focus-visible:ring-[3px]',
            open && 'bg-accent',
          )}
          type="button"
        >
          {label}
          <ChevronsUpDown className="size-3.5 shrink-0 text-neutral-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command>
          <CommandInput placeholder="Search companies…" />
          <CommandList>
            <CommandEmpty>No companies found.</CommandEmpty>
            <CommandGroup>
              {availableCompanies.map((company) => (
                <CommandItem
                  key={company.id}
                  onSelect={() => {
                    setOpen(false);
                    onChange(company.id);
                  }}
                  value={`${company.name} ${company.id}`}
                >
                  <span className="flex-1 truncate">{company.name}</span>
                  {company.id === selectedTenantId && (
                    <Check className="text-primary size-4" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
